/**
 * PATH: packages/email-hub/src/feature/pages/EmailViewPage.tsx
 * FILE_ID: EMAILHUB-FEATURE-PAGES-EMAILVIEWPAGE-001
 * PURPOSE: Canonical body of this email pages module. Lifted
 *          from apps/<tenant>/src/features/email/pages/EmailViewPage.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/pages/EmailViewPage.tsx: thin re-export
 *     shim that points back here.
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useSmartBack } from '@quotech/ui'

import EmailReader from '../components/EmailReader'
import { emailPath } from '../lib/basePath'
import { useEmailAllocationMembers } from '../hooks/useEmailMembers'

export default function EmailViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // BUG-2026-07-02-KANBAN-EMAIL-BACK-LOST — the reader's Back was hard-wired to
  // `/inbox`, so opening an email FROM a Kanban board (icon rail / card action
  // → navigate('/email/<id>')) and hitting Back dumped the operator on the
  // inbox instead of returning to the board they came from. `useSmartBack`
  // pops in-app history (navigate(-1)) when there IS a prior in-app entry — so
  // Kanban → email → Back lands back on the exact board (URL preserved) — and
  // only falls back to the inbox when the email was opened directly /
  // deep-linked (no in-app history to pop). The Kanban open path already
  // PUSHES (not replaces) history, so the board entry is there to return to.
  const { goBack } = useSmartBack(emailPath('/inbox'))
  // Email→Quote ARC § Phase 10 — resolve org teammates for the
  // EmailAllocationRail (assignee picker + watcher invite). Null
  // while loading; once resolved (even if empty), the rail
  // renders. See packages/email-hub/src/feature/hooks/useEmailMembers.ts.
  const allocationMembers = useEmailAllocationMembers()

  return (
    <EmailReader
      emailId={id ?? null}
      onBack={goBack}
      backLabel="Back"
      onArchived={() => navigate(emailPath('/inbox'))}
      onTrashed={() => navigate(emailPath('/inbox'))}
      allocationMembers={allocationMembers ?? undefined}
    />
  )
}
